import { useCallback, useEffect, useState } from "react";
import { createDocumentPdf } from "../utils/documentPdf";

/** One immutable PDF per document revision, shared by preview, download and email. */
export default function useDocumentPdf(contentRef, revision, filename, enabled = true) {
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const current = enabled && result?.revision === revision && result?.attempt === attempt;
  const ready = Boolean(current && result?.blob);

  useEffect(() => {
    setResult(null);
    if (!enabled) return undefined;
    let active = true;
    let url;
    const generate = async () => {
      try {
        const pdf = await createDocumentPdf(contentRef.current);
        if (!active) return;
        const blob = pdf.output("blob");
        if (!(blob instanceof Blob) || !blob.size) throw new Error("The generated PDF is empty");
        url = URL.createObjectURL(blob);
        setResult({ revision, attempt, blob, url });
      } catch (error) {
        if (active) setResult({ revision, attempt, error });
      }
    };
    generate();
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [contentRef, revision, enabled, attempt]);

  const getBlob = useCallback(() => {
    if (!ready) throw new Error("The PDF preview is not ready");
    return result.blob;
  }, [ready, result]);

  const download = useCallback(() => {
    getBlob();
    const link = document.createElement("a");
    link.href = result.url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [getBlob, result, filename]);

  return {
    ready,
    url: ready ? result.url : null,
    error: current ? result.error : null,
    getBlob,
    download,
    retry: () => setAttempt(value => value + 1),
  };
}
